'use client'

import { useRef } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { StorefrontProduct } from '@sifly/shared-types'
import { cropSquareUrl } from '@sifly/shared-utils'
import { CardPrice, SaleBadge } from './CardPrice'

export type ProductCarouselProps = {
  products: StorefrontProduct[]
  title?: string
}

export function ProductCarousel({ products, title }: ProductCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null)

  function scroll(direction: 'left' | 'right') {
    if (!scrollRef.current) return
    const amount = scrollRef.current.clientWidth * 0.8
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    })
  }

  return (
    <section data-component="ProductCarousel" className="relative">
      {title && (
        <h2
          className="text-xl font-semibold text-[var(--color-text-primary)] mb-4"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          {title}
        </h2>
      )}

      {/* Navigation arrows */}
      <button
        onClick={() => scroll('left')}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 hidden md:flex items-center justify-center w-10 h-10 rounded-full bg-[var(--color-bg-primary)] border border-[var(--color-border)] shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow"
        aria-label="Scroll left"
      >
        <ChevronLeft size={20} className="text-[var(--color-text-primary)]" />
      </button>
      <button
        onClick={() => scroll('right')}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 hidden md:flex items-center justify-center w-10 h-10 rounded-full bg-[var(--color-bg-primary)] border border-[var(--color-border)] shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow"
        aria-label="Scroll right"
      >
        <ChevronRight size={20} className="text-[var(--color-text-primary)]" />
      </button>

      {/* Scrollable row */}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4 -mx-4 px-4 scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {products.map((product) => {
          const cardImage = cropSquareUrl(product.photos[0])
          return (
          <Link
            key={product.id}
            href={`/products/${product.slug ?? product.id}`}
            className="snap-start shrink-0 w-[calc(66.67%-0.5rem)] md:w-[calc(33.33%-0.67rem)] group rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-bg-primary)] overflow-hidden hover:shadow-[var(--shadow-md)] transition-shadow"
          >
            <div className="relative aspect-square bg-[var(--color-bg-secondary)]">
              {cardImage && (
                <Image
                  src={cardImage}
                  alt={product.name}
                  fill
                  sizes="(max-width: 768px) 66vw, 33vw"
                  className="object-cover"
                />
              )}
              {product.onSale && <SaleBadge className="absolute left-2 top-2" />}
            </div>
            <div className="p-3">
              <h3
                className="text-sm font-medium text-[var(--color-text-primary)] line-clamp-1"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                {product.name}
              </h3>
              {/* Short description as a subtitle — without it, same-category
                  siblings at the same price (five wings at $650) are
                  indistinguishable. Already dealer-override-aware: the
                  adapter's applyOverlayToProduct ran before this renders. */}
              {product.description && (
                <p className="mt-0.5 text-xs text-[var(--color-text-muted)] line-clamp-2">
                  {product.description}
                </p>
              )}
              <CardPrice
                displayPrice={product.displayPrice}
                compareAtPrice={product.compareAtPrice}
                priceFromMultiple={product.priceFromMultiple}
                onSale={product.onSale}
                className="mt-1 text-sm text-[var(--color-text-secondary)]"
              />
            </div>
          </Link>
          )
        })}
      </div>
    </section>
  )
}
